(() => {
    const settings = window.gwpSettings || {};
    const giftVariantId = Number(settings.variantId);
    const threshold = Number(settings.threshold);
    const thresholdActive = String(settings.thresholdRaw ?? '').trim() !== '' && !Number.isNaN(threshold) && threshold > 0;
    const requiredProductIds = (Array.isArray(settings.requiredProductIds) ? settings.requiredProductIds : [])
        .map(Number)
        .filter((id) => !Number.isNaN(id) && id > 0);
    const productConditionActive = requiredProductIds.length > 0;
    const matchMode = settings.matchMode === 'all' ? 'all' : 'any';
    const discountCode = String(settings.discountCode || '').trim();
    const trackingLabel = String(settings.trackingLabel || '').trim();

    // Second, optional gift: threshold-only condition, independent of gift 1's
    // match mode / required-products conditions.
    const giftVariantId2 = Number(settings.variantId2);
    const threshold2 = Number(settings.threshold2);
    const threshold2Active = String(settings.threshold2Raw ?? '').trim() !== '' && !Number.isNaN(threshold2) && threshold2 > 0;
    const gift2Configured = Number.isFinite(giftVariantId2) && giftVariantId2 > 0 && threshold2Active;
    const discountCode2 = String(settings.discountCode2 || '').trim();

    // Require: feature on, a gift configured, and at least one active trigger condition.
    if (!settings.enabled || !giftVariantId) return;
    if (!thresholdActive && !productConditionActive && !gift2Configured) return;

    const allGiftVariantIds = [giftVariantId, ...(gift2Configured ? [giftVariantId2] : [])].filter((id) => Number.isFinite(id) && id > 0);

    const automaticGiftProperty = '_automatic_gwp';
    const trackingAttribute = 'gwp_promo';
    const cartMutationPaths = [
        window.routes?.cart_add_url,
        window.routes?.cart_change_url,
        window.routes?.cart_update_url,
        '/cart/add',
        '/cart/add.js',
        '/cart/change',
        '/cart/change.js',
        '/cart/update',
        '/cart/update.js',
        '/cart/clear',
        '/cart/clear.js',
    ].filter(Boolean);
    let isSyncing = false;
    let shouldSyncAgain = false;
    const requestTimeout = 10000;
    const originalFetch = window.fetch;
    const cartRoot = window.Shopify?.routes?.root || '/';

    const fetchWithTimeout = (resource, options = {}) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

        return originalFetch.call(window, resource, {
            ...options,
            signal: controller.signal,
        }).finally(() => clearTimeout(timeoutId));
    };

    const getPathname = (resource) => {
        try {
            const url = typeof resource === 'string' ? resource : resource.url;
            return new URL(url, window.location.origin).pathname;
        } catch (error) {
            console.error('Error parsing cart request URL for GWP:', error);
            return '';
        }
    };

    const isCartMutation = (resource) => {
        const pathname = getPathname(resource);

        return cartMutationPaths.some((path) => pathname === path || pathname.endsWith(path));
    };

    const requestCart = () => fetchWithTimeout(`${cartRoot}cart.js`, {
        headers: {
            Accept: 'application/json',
        },
    }).then(async (response) => {
        if (!response.ok) throw new Error(`Unable to read cart (${response.status}).`);
        return response.json();
    });

    // Excludes every configured gift's line total from the cart total, so
    // adding one gift never affects the eligibility check for the other.
    const getEligibleTotal = (cart) => {
        const giftsTotal = cart.items
            .filter((item) => allGiftVariantIds.includes(item.variant_id))
            .reduce((total, item) => total + item.final_line_price, 0);

        return cart.total_price - giftsTotal;
    };

    // Evaluates gift 1's configured trigger conditions against the current cart.
    // Only conditions that are filled in by the merchant are evaluated; the
    // active ones are combined with AND (matchMode 'all') or OR (matchMode 'any').
    const isGift1Eligible = (cart) => {
        const conditions = [];

        if (thresholdActive) {
            const eligibleTotal = getEligibleTotal(cart);
            conditions.push(eligibleTotal > 0 && eligibleTotal >= threshold);
        }

        if (productConditionActive) {
            const productIdsInCart = new Set(
                cart.items
                    .filter((item) => !allGiftVariantIds.includes(item.variant_id))
                    .map((item) => item.product_id)
            );
            conditions.push(requiredProductIds.every((id) => productIdsInCart.has(id)));
        }

        if (!conditions.length) return false;

        return matchMode === 'all'
            ? conditions.every(Boolean)
            : conditions.some(Boolean);
    };

    // Gift 2 is a simple, independent cart-total threshold.
    const isGift2Eligible = (cart) => {
        if (!gift2Configured) return false;

        const eligibleTotal = getEligibleTotal(cart);
        return eligibleTotal > 0 && eligibleTotal >= threshold2;
    };

    const giftDefinitions = [
        { variantId: giftVariantId, isEligible: isGift1Eligible, discountCode, trackingLabel },
    ];

    if (gift2Configured) {
        giftDefinitions.push({
            variantId: giftVariantId2,
            isEligible: isGift2Eligible,
            discountCode: discountCode2,
            trackingLabel,
        });
    }

    const publishCartUpdate = (cart) => {
        if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'automatic-gwp',
            cartData: cart,
            variantId: giftVariantId,
        });
    };

    const refreshCartRecommendations = () => {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
            detail: {
                source: 'automatic-gwp',
            },
        }));
    };

    const setCartLoading = (isLoading) => {
        const cartContainers = [
            document.getElementById('CartDrawer-CartItems'),
            document.getElementById('main-cart-items'),
            document.querySelector('cart-drawer-items'),
            document.querySelector('cart-items'),
        ].filter(Boolean);
        const statusElement = document.getElementById('CartDrawer-LineItemStatus') ||
            document.getElementById('shopping-cart-line-item-status');

        cartContainers.forEach((container) => {
            container.classList.toggle('cart__items--disabled', isLoading);
        });

        if (statusElement) {
            statusElement.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
        }

        if (isLoading) {
            document.querySelectorAll('#CartDrawer-CartItems .loading__spinner, #main-cart-items .loading__spinner')
                .forEach((spinner) => {
                    if (spinner.classList.contains('hidden')) {
                        spinner.dataset.automaticGwpLoading = 'true';
                        spinner.classList.remove('hidden');
                    }
                });
            return;
        }

        document.querySelectorAll('[data-automatic-gwp-loading="true"]').forEach((spinner) => {
            spinner.classList.add('hidden');
            delete spinner.dataset.automaticGwpLoading;
        });
    };

    const hasDiscountCode = (cart, code) => {
        if (!code) return true;

        const normalizedDiscountCode = code.toLowerCase();
        const discountApplications = [
            ...(cart.cart_level_discount_applications || []),
            ...(cart.items || []).flatMap((item) => [
                ...(item.discount_allocations || []),
                ...(item.line_level_discount_allocations || []),
            ]),
            ...(cart.discount_codes || []).filter((discount) => discount.applicable !== false),
        ];

        return discountApplications.some((discount) => {
            const title = String(
                discount.title ||
                discount.code ||
                discount.discount_application?.title ||
                discount.discount_application?.code ||
                ''
            ).toLowerCase();
            return title === normalizedDiscountCode;
        });
    };

    const addGift = (variantId, giftTrackingLabel) => fetchWithTimeout(`${cartRoot}cart/add.js`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: {
                // Marks the line as auto-added and carries the campaign tracking
                // label (falls back to 'true') so GWP orders/units are reportable.
                [automaticGiftProperty]: giftTrackingLabel || 'true',
            },
        }),
    });

    // Stamps a cart attribute so the order is taggable/reportable in Shopify even
    // if the gift line item properties are not surfaced in a given report.
    const setTrackingAttribute = (giftTrackingLabel) => {
        if (!giftTrackingLabel) return Promise.resolve();

        return fetchWithTimeout(`${cartRoot}cart/update.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({
                attributes: {
                    [trackingAttribute]: giftTrackingLabel,
                },
            }),
        }).then((response) => {
            if (!response.ok) throw new Error(`Unable to set GWP tracking (${response.status}).`);
        }).catch((error) => {
            console.error('Error setting GWP tracking attribute:', error);
        });
    };

    const applyDiscountCodes = async (cart, gifts) => {
        const applications = [
            ...(cart.cart_level_discount_applications || []),
            ...cart.items.flatMap((item) => [
                ...(item.discount_allocations || []),
                ...(item.line_level_discount_allocations || []),
            ]),
        ].map((entry) => entry.discount_application || entry);
        const codes = [
            ...(cart.discount_codes || []).map((entry) => entry.code),
            ...applications.filter((entry) => entry.type === 'discount_code')
                .map((entry) => entry.code || entry.title),
            ...gifts.map((gift) => gift.discountCode),
        ].filter(Boolean);
        const uniqueCodes = [...new Map(codes.map((code) => [code.toLowerCase(), code])).values()];
        const response = await fetchWithTimeout(`${cartRoot}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: uniqueCodes.join(',') }),
        });
        if (!response.ok) throw new Error(`Unable to apply GWP discounts (${response.status}).`);
        return response.json();
    };

    const syncGift = async () => {
        if (isSyncing) {
            shouldSyncAgain = true;
            return;
        }

        isSyncing = true;
        let didSetCartLoading = false;
        let cartChanged = false;

        try {
            let cart = await requestCart();

            // Add-only: never auto-removes a gift if the cart later drops below
            // its trigger, to avoid disrupting the shopper's cart flow. Each gift
            // is evaluated independently, so either or both can be added in one pass.
            for (const gift of giftDefinitions) {
                const hasGiftProduct = cart.items.some((item) => item.variant_id === gift.variantId);
                const isEligible = gift.isEligible(cart);

                if (!isEligible || hasGiftProduct) continue;

                if (!didSetCartLoading) {
                    setCartLoading(true);
                    didSetCartLoading = true;
                }

                const response = await addGift(gift.variantId, gift.trackingLabel);

                if (!response.ok) {
                    console.error(`Unable to add GWP variant ${gift.variantId}:`, await response.text());
                    continue;
                }

                cartChanged = true;
                await setTrackingAttribute(gift.trackingLabel);
                cart = await requestCart();
            }

            // Submit all gift codes together, including gifts already in the cart.
            // This also repairs a previous add whose discount request failed.
            const giftsWithCodes = giftDefinitions.filter((gift) => gift.discountCode &&
                cart.items.some((item) => item.variant_id === gift.variantId &&
                    (item.properties?.[automaticGiftProperty] || gift.isEligible(cart))));
            if (giftsWithCodes.some((gift) => !hasDiscountCode(cart, gift.discountCode))) {
                setCartLoading(true);
                didSetCartLoading = true;
                cart = await applyDiscountCodes(cart, giftsWithCodes);
                cartChanged = true;
                for (const gift of giftsWithCodes) {
                    if (!hasDiscountCode(cart, gift.discountCode)) {
                        console.error(`GWP discount ${gift.discountCode} was not applied. Check its eligibility and discount combinations in Shopify.`);
                    }
                }
            }

            if (cartChanged) {
                publishCartUpdate(cart);
                refreshCartRecommendations();
            }

        } catch (error) {
            console.error('Error syncing automatic gift with purchase:', error);
            if (cartChanged) {
                try {
                    publishCartUpdate(await requestCart());
                    refreshCartRecommendations();
                } catch (refreshError) {
                    console.error('Error refreshing cart after GWP update:', refreshError);
                }
            }
        } finally {
            if (didSetCartLoading) {
                setCartLoading(false);
            }
            isSyncing = false;

            if (shouldSyncAgain) {
                shouldSyncAgain = false;
                syncGift();
            }
        }
    };

    window.fetch = function (...args) {
        const response = originalFetch.apply(this, args);

        if (isCartMutation(args[0])) {
            response.then(async (result) => {
                if (!result.ok) return;
                await result.clone().text();
                if (isSyncing) {
                    shouldSyncAgain = true;
                    return;
                }

                syncGift();
            }).catch(() => {});
        }

        return response;
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', syncGift, { once: true });
    } else {
        syncGift();
    }
})();
